import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ListenButton from "../components/ListenButton";
import ReadAloud from "../components/ReadAloud";
import { useAuth } from "../contexts/AuthContext";

export default function Lesson() {
    const { pillarId, lessonId } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [data, setData] = useState(null);
    const [marking, setMarking] = useState(false);
    const [done, setDone] = useState(false);
    // Read-aloud mastery gate: once a learner has attempted the read-aloud,
    // they must reach 80% to unlock "Mark complete". Not attempting = no gate.
    const [readAloudLocked, setReadAloudLocked] = useState(false);

    useEffect(() => {
        api.get(`/curriculum/${pillarId}/lesson/${lessonId}`).then((r) => setData(r.data));
    }, [pillarId, lessonId]);

    const complete = async () => {
        if (!data) return;
        setMarking(true);
        try {
            const { data: res } = await api.post("/progress/complete-lesson", {
                lesson_id: data.lesson.id,
                xp: data.lesson.xp,
            });
            setDone(true);
            if (res.freeze_used) {
                toast.success("Streak save used — your streak is safe", { description: `${res.streak_freezes} ${res.streak_freezes === 1 ? "save" : "saves"} left.` });
            } else if (res.awarded > 0) {
                toast.success(`Lesson complete — +${res.awarded} XP`);
            } else {
                toast.success("Reviewed — well done");
            }
            refresh();
        } catch (e) {
            toast.error("Could not save progress");
        } finally {
            setMarking(false);
        }
    };

    if (!data) return <main className="container-page py-16">Loading…</main>;

    const { pillar, lesson } = data;

    return (
        <main className="container-page py-12 max-w-3xl fade-up">
            <Link to={`/app/curriculum/${pillar.id}`} className="inline-flex items-center gap-2 text-[#515B57] hover:text-[#2D5D4E] mb-6" data-testid="lesson-back">
                <ArrowLeft className="w-4 h-4" /> Back to {pillar.title}
            </Link>

            <p className="label-eyebrow mb-2">{pillar.title}</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-3" style={{ fontFamily: "Outfit" }} data-testid="lesson-title">{lesson.title}</h1>
            <p className="text-lg text-[#515B57] mb-8">{lesson.objective}</p>

            <article className="card-rw p-8 md:p-10 mb-8">
                <p className="text-lg leading-[1.75] text-[#1C2321] whitespace-pre-line" data-testid="lesson-content">
                    {lesson.content}
                </p>
                <div className="mt-6">
                    <ListenButton text={lesson.content} testId="lesson-listen-content" label="Listen to this lesson" size="md" />
                </div>
            </article>

            <div className="mb-8">
                <ReadAloud
                    expectedText={lesson.content}
                    onGateChange={({ locked }) => setReadAloudLocked(locked)}
                />
            </div>

            {lesson.examples?.length > 0 && (
                <section className="mb-8">
                    <p className="label-eyebrow mb-4">Examples</p>
                    <div className="space-y-3">
                        {lesson.examples.map((ex, i) => (
                            <div key={i} className="card-rw p-5 flex items-center justify-between gap-4" data-testid={`lesson-example-${i}`}>
                                <p className="text-xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{ex}</p>
                                <ListenButton text={ex.replace(/\//g, "")} testId={`lesson-example-listen-${i}`} size="sm" label="Listen" />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {lesson.practice_words?.length > 0 && (
                <section className="mb-12">
                    <p className="label-eyebrow mb-4">Practice these words</p>
                    <div className="flex flex-wrap gap-3">
                        {lesson.practice_words.map((w, i) => (
                            <div key={i} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-[#E5E7E2]" data-testid={`practice-word-${i}`}>
                                <span className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>{w}</span>
                                <ListenButton text={w} testId={`practice-listen-${i}`} size="sm" label="" />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <div className="flex flex-wrap gap-4 items-center">
                <button
                    onClick={complete}
                    disabled={marking || done || readAloudLocked}
                    data-testid="lesson-complete-button"
                    data-locked={readAloudLocked ? "true" : "false"}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {done ? <><CheckCircle2 className="w-5 h-5" /> Lesson complete</> : marking ? "Saving…" : `Mark complete · +${lesson.xp} XP`}
                </button>
                <button
                    onClick={() => navigate(`/app/curriculum/${pillar.id}`)}
                    data-testid="lesson-next-button"
                    className="px-7 py-3.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] text-lg font-medium hover:bg-[#E8E2D2]"
                >
                    Back to pillar
                </button>
                {readAloudLocked && !done && (
                    <p className="text-sm text-[#8F3636] flex-basis-full w-full mt-1" data-testid="lesson-complete-locked-hint">
                        Read the passage aloud with at least <span className="font-semibold">80%</span> accuracy above to mark this lesson complete.
                    </p>
                )}
            </div>
        </main>
    );
}
